import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../LoadingSpinner';
import { GoogleLogin } from './GoogleLogin';

interface RegisterProps {
  onSwitchToLogin?: () => void;
  onSuccess?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const { register, loading, error, clearError } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) clearError();
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const validationError = validateForm();
    if (validationError) {
      return;
    }

    try {
      await register(
        formData.name,
        formData.email,
        formData.password,
        formData.confirmPassword
      );
      setRegistrationSuccess(true);
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-dark-bg via-slate-800 to-dark-bg p-4 sm:p-5">
        <div className="bg-dark-card rounded-2xl shadow-dark-xl p-8 sm:p-10 w-full max-w-[480px] animate-fade-in border border-dark-border">
          <div className="text-center mb-8">
            <div className="text-7xl mb-5">✅</div>
            <h1 className="text-[28px] font-bold text-dark-text m-0 mb-4">Registration Successful!</h1>
            <p className="text-lg text-dark-text-secondary m-0 leading-relaxed">
              Please check your email to verify your account before logging in.
            </p>
          </div>

          <div className="mb-8">
            <div className="bg-teal-500/10 border border-teal-400/30 rounded-lg p-4 text-center mb-6">
              <p className="text-teal-300 m-0 mb-2 text-sm">
                We've sent a verification email to:
              </p>
              <p className="text-teal-300 font-bold text-base m-0 break-all">{formData.email}</p>
            </div>

            <div className="bg-dark-bg-tertiary rounded-lg p-5">
              <h3 className="text-dark-text m-0 mb-4 text-lg">Next Steps:</h3>
              <ol className="text-dark-text-secondary m-0 pl-5 leading-relaxed">
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the verification link in the email</li>
                <li>Return here to log in</li>
              </ol>
            </div>
          </div>

          <div className="text-center">
            {onSwitchToLogin ? (
              <button
                onClick={onSwitchToLogin}
                className="bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-lg py-3 px-6 text-base font-semibold cursor-pointer transition-all duration-200 hover:opacity-90"
              >
                Back to Login
              </button>
            ) : (
              <button
                onClick={onSwitchToLogin}
                className="bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-lg py-3 px-6 text-base font-semibold cursor-pointer inline-block transition-all duration-200 hover:opacity-90"
              >
                Back to Login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-dark-bg via-slate-800 to-dark-bg p-4 sm:p-5">
      <div className="bg-dark-card rounded-2xl shadow-dark-xl p-8 sm:p-10 w-full max-w-[420px] animate-fade-in border border-dark-border">
        <div className="text-center mb-8">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white text-[28px] font-bold mx-auto shadow-[0_8px_20px_rgba(251,191,36,0.3)]">
              O
            </div>
          </div>
          <h1 className="text-[28px] font-bold text-dark-text m-0 mb-2">Create Account</h1>
          <p className="text-base text-dark-text-secondary m-0">Join OBusinessCard and start networking</p>
        </div>

        {/* Fastest sign-up: Google creates the account and signs in immediately */}
        <GoogleLogin onSuccess={onSuccess} onError={() => undefined} />

        <div className="flex items-center my-6">
          <span className="flex-1 h-px bg-slate-600/50"></span>
          <span className="px-4 text-sm text-dark-text-secondary">or sign up with email</span>
          <span className="flex-1 h-px bg-slate-600/50"></span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-2 text-red-300 text-sm">
              <span className="text-base">⚠️</span>
              {error}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-dark-text">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="py-3 px-4 border-2 border-dark-border rounded-lg text-base transition-all duration-200 outline-none bg-dark-bg-tertiary text-dark-text focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-dark-text">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="py-3 px-4 border-2 border-dark-border rounded-lg text-base transition-all duration-200 outline-none bg-dark-bg-tertiary text-dark-text focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-dark-text">Password</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Choose a secure password"
                className="py-3 pl-4 pr-[50px] border-2 border-dark-border rounded-lg text-base w-full transition-all duration-200 outline-none bg-dark-bg-tertiary text-dark-text focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 bg-none border-none cursor-pointer text-lg p-1 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <p className="text-xs text-dark-text-secondary m-0 -mt-0.5">Must be at least 6 characters</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-dark-text">Confirm Password</label>
            <div className="relative flex items-center">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="py-3 pl-4 pr-[50px] border-2 border-dark-border rounded-lg text-base w-full transition-all duration-200 outline-none bg-dark-bg-tertiary text-dark-text focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 bg-none border-none cursor-pointer text-lg p-1 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword}
            className={`w-full bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-lg py-3.5 px-6 text-base font-semibold cursor-pointer transition-all duration-200 mt-2 ${
              loading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:opacity-90 hover:-translate-y-0.5'
            }`}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="small" label="Creating account" />
                Creating Account...
              </span>
            ) : 'Create Account'}
          </button>
        </form>

        <div className="text-center mt-8 pt-6 border-t border-dark-border">
          <p className="text-dark-text-secondary text-sm m-0">
            Already have an account?{' '}
            {onSwitchToLogin ? (
              <button
                onClick={onSwitchToLogin}
                className="text-amber-400 bg-none border-none text-sm font-semibold cursor-pointer no-underline p-0 hover:text-amber-300"
              >
                Sign In
              </button>
            ) : (
              <button
                onClick={onSwitchToLogin}
                className="text-amber-400 bg-none border-none cursor-pointer no-underline font-semibold p-0 text-sm hover:text-amber-300"
              >
                Sign In
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
